// src/components/clients/ClientAdditionalServices.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CompleteIcon
} from '@mui/icons-material';

const AssignServiceDialog = ({ open, onClose, onSubmit, catalogServices, assignment = null }) => {
  const [formData, setFormData] = useState({
    serviceId: '',
    price: '',
    billingType: 'monthly',
    status: 'active'
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (assignment) {
        setFormData({
          serviceId: assignment.service_id,
          price: assignment.price,
          billingType: assignment.billing_type,
          status: assignment.status
        });
      } else {
        setFormData({
          serviceId: '',
          price: '',
          billingType: 'monthly',
          status: 'active'
        });
      }
      setError('');
    }
  }, [open, assignment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Si cambia el servicio seleccionado y no es edición, pre-rellenar el precio base del catálogo
    if (name === 'serviceId' && !assignment) {
      const selectedService = catalogServices.find(s => s.id === value);
      if (selectedService) {
        setFormData(prev => ({
          ...prev,
          serviceId: value,
          price: selectedService.price
        }));
      }
    }
  };

  const handleSubmit = () => {
    if (!formData.serviceId || formData.price === '') {
      setError('Por favor, complete todos los campos requeridos');
      return;
    }
    onSubmit(formData);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: '20px', p: 1 }
      }}
    >
      <DialogTitle sx={{ fontWeight: 800, pb: 1, color: '#0f172a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{assignment ? 'Editar Servicio Asignado' : 'Asignar Servicio Adicional'}</span>
        <IconButton onClick={onClose} size="small" sx={{ color: '#64748b' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {error && (
            <Typography color="error" variant="body2" sx={{ fontWeight: 'bold' }}>
              ⚠️ {error}
            </Typography>
          )}

          <FormControl fullWidth disabled={!!assignment}>
            <InputLabel id="service-select-label">Servicio del Catálogo *</InputLabel>
            <Select
              labelId="service-select-label"
              name="serviceId"
              value={formData.serviceId}
              onChange={handleChange}
              label="Servicio del Catálogo *"
              sx={{ borderRadius: '12px' }}
            >
              <MenuItem value="">Seleccione un servicio</MenuItem>
              {catalogServices.map(service => (
                <MenuItem key={service.id} value={service.id}>
                  {service.name} - Tarifa Base: S/ {parseFloat(service.price).toFixed(2)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            type="number"
            label="Precio Cobrado *"
            name="price"
            value={formData.price}
            onChange={handleChange}
            inputProps={{ step: '0.01', min: '0' }}
            placeholder="Ej. S/ 35.00"
            required
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />

          <FormControl fullWidth>
            <InputLabel id="billing-type-label">Tipo de Facturación *</InputLabel>
            <Select
              labelId="billing-type-label"
              name="billingType"
              value={formData.billingType}
              onChange={handleChange}
              label="Tipo de Facturación *"
              sx={{ borderRadius: '12px' }}
            >
              <MenuItem value="monthly">Mensual (Recurrente)</MenuItem>
              <MenuItem value="one_time">Pago Único</MenuItem>
            </Select>
          </FormControl>

          {assignment && (
            <FormControl fullWidth>
              <InputLabel id="status-label">Estado *</InputLabel>
              <Select
                labelId="status-label"
                name="status"
                value={formData.status}
                onChange={handleChange}
                label="Estado *"
                sx={{ borderRadius: '12px' }}
              >
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="inactive">Pausado (Inactivo)</MenuItem>
                <MenuItem value="completed">Completado (Pago Único)</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button 
          onClick={onClose}
          sx={{ borderRadius: '12px', textTransform: 'none', color: '#64748b', fontWeight: 600 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            px: 4,
            fontWeight: 700,
            backgroundColor: '#6366f1',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
            '&:hover': { backgroundColor: '#4f46e5' }
          }}
        >
          {assignment ? 'Guardar Cambios' : 'Asignar Servicio'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ClientAdditionalServices = ({ clientId }) => {
  const [assignedServices, setAssignedServices] = useState([]);
  const [catalogServices, setCatalogServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);

  useEffect(() => {
    if (clientId) {
      fetchData();
    }
  }, [clientId]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      // 1. Obtener servicios asignados al cliente
      const assignedRes = await fetch(`/api/clients/${clientId}/additional-services`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!assignedRes.ok) {
        throw new Error('Error al obtener servicios adicionales asignados');
      }
      const assignedData = await assignedRes.json();
      setAssignedServices(assignedData);

      // 2. Obtener catálogo global de servicios
      const catalogRes = await fetch('/api/finance/billing-services', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!catalogRes.ok) {
        throw new Error('Error al obtener catálogo de servicios');
      }
      const catalogData = await catalogRes.json();
      setCatalogServices(catalogData);
    } catch (err) {
      console.error('Error fetching additional services data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrEdit = async (formData) => {
    try {
      const payload = {
        service_id: formData.serviceId,
        price: parseFloat(formData.price),
        billing_type: formData.billingType,
        status: formData.status
      };

      let response;
      if (selectedAssignment) {
        // Editar
        response = await fetch(`/api/clients/${clientId}/additional-services/${selectedAssignment.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      } else {
        // Crear
        response = await fetch(`/api/clients/${clientId}/additional-services`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      }

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Error al guardar asignación de servicio');
      }

      setOpenDialog(false);
      setSelectedAssignment(null);
      fetchData();
    } catch (err) {
      console.error('Error saving assignment:', err);
      alert(err.message);
    }
  };

  const handleToggleStatus = async (assignment) => {
    try {
      const newStatus = assignment.status === 'active' ? 'inactive' : 'active';
      const response = await fetch(`/api/clients/${clientId}/additional-services/${assignment.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          price: assignment.price,
          billing_type: assignment.billing_type,
          status: newStatus
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Error al cambiar estado del servicio');
      }

      fetchData();
    } catch (err) {
      console.error('Error toggling service status:', err);
      alert(err.message);
    }
  };

  const handleDelete = async () => {
    if (!assignmentToDelete) return;
    try {
      const response = await fetch(`/api/clients/${clientId}/additional-services/${assignmentToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Error al eliminar el servicio asignado');
      }

      setDeleteConfirmOpen(false);
      setAssignmentToDelete(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting assignment:', err);
      alert(err.message);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
            Servicios Complementarios y Streaming Asignados
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Vincule servicios como Netflix, soporte técnico, o alquileres a la facturación mensual del cliente.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedAssignment(null);
            setOpenDialog(true);
          }}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            backgroundColor: '#6366f1',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
            '&:hover': { backgroundColor: '#4f46e5' }
          }}
        >
          Asignar Servicio
        </Button>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2, fontWeight: 'bold' }}>
          ⚠️ {error}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.05)' }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Servicio</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Precio Cobrado</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Tipo de Cobro</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Estado</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignedServices.length > 0 ? (
                assignedServices.map((item) => (
                  <TableRow key={item.id} sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {item.service_name}
                      </Typography>
                      {item.service_description && (
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {item.service_description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>
                      S/ {parseFloat(item.price).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.billing_type === 'monthly' ? 'Mensual' : 'Pago Único'}
                        variant="outlined"
                        size="small"
                        sx={{
                          borderColor: item.billing_type === 'monthly' ? '#6366f1' : '#f59e0b',
                          color: item.billing_type === 'monthly' ? '#6366f1' : '#d97706',
                          fontWeight: 600,
                          borderRadius: '8px'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          item.status === 'active' 
                            ? 'Activo' 
                            : item.status === 'inactive' 
                              ? 'Pausado' 
                              : 'Completado'
                        }
                        size="small"
                        sx={{
                          backgroundColor: 
                            item.status === 'active' 
                              ? '#dcfce7' 
                              : item.status === 'inactive' 
                                ? '#fee2e2' 
                                : '#f1f5f9',
                          color: 
                            item.status === 'active' 
                              ? '#15803d' 
                              : item.status === 'inactive' 
                                ? '#b91c1c' 
                                : '#475569',
                          fontWeight: 700,
                          borderRadius: '8px'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        {item.status !== 'completed' && (
                          <Tooltip title={item.status === 'active' ? 'Pausar Servicio' : 'Activar Servicio'}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleStatus(item)}
                              sx={{
                                color: item.status === 'active' ? '#f59e0b' : '#10b981',
                                backgroundColor: item.status === 'active' ? 'rgba(245, 158, 11, 0.06)' : 'rgba(16, 185, 129, 0.06)',
                                '&:hover': {
                                  backgroundColor: item.status === 'active' ? 'rgba(245, 158, 11, 0.12)' : 'rgba(16, 185, 129, 0.12)'
                                }
                              }}
                            >
                              {item.status === 'active' ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedAssignment(item);
                              setOpenDialog(true);
                            }}
                            sx={{
                              color: '#6366f1',
                              backgroundColor: 'rgba(99, 102, 241, 0.06)',
                              '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.12)' }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setAssignmentToDelete(item);
                              setDeleteConfirmOpen(true);
                            }}
                            sx={{
                              backgroundColor: 'rgba(239, 68, 68, 0.06)',
                              '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.12)' }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6, color: '#64748b' }}>
                    No hay servicios adicionales asignados a este cliente.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialogo de Asignación / Edición */}
      <AssignServiceDialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setSelectedAssignment(null);
        }}
        onSubmit={handleAddOrEdit}
        catalogServices={catalogServices}
        assignment={selectedAssignment}
      />

      {/* Confirmar Eliminación */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setAssignmentToDelete(null);
        }}
        PaperProps={{
          sx: { borderRadius: '20px', p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1, color: '#0f172a' }}>
          Desasignar Servicio
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#475569', fontWeight: 500 }}>
            ¿Estás seguro de que deseas desasignar el servicio "{assignmentToDelete?.service_name}" de este cliente?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setAssignmentToDelete(null);
            }}
            sx={{ borderRadius: '12px', textTransform: 'none', color: '#64748b', fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            sx={{ borderRadius: '12px', textTransform: 'none', px: 3, fontWeight: 700 }}
          >
            Desasignar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientAdditionalServices;
